import { useEffect } from 'react';
import SEOHead from '../../components/SEOHead';
import { trackMetaPageView } from "@/lib/meta";
import { getMergedUtmParams, UTM_SOURCE, saveUtmParams } from '@/lib/utm';

const APP_STORE_URL = "https://apps.apple.com/app/nomikoi-%E9%A3%B2%E3%81%BF%E3%81%AB%E8%A1%8C%E3%81%8F%E3%81%8D%E3%81%A3%E3%81%8B%E3%81%91%E3%81%8C%E8%A6%8B%E3%81%A4%E3%81%8B%E3%82%8B/id6759240144";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.mavniq.nomikoi";
const PAGE_TITLE = 'nomikoi - 飲みに行くきっかけが見つかる - 飲みたい気分を共有できるSNSアプリ';
const PAGE_DESCRIPTION = '普段飲みに行く街、行ってみたいお店、気になるイベント、今日の飲みたい気分をきっかけに、人とつながれるアプリです。飲む場所やペース、好きなお酒、好きなお店の雰囲気など、普段の飲み方や過ごし方も見えるのが特徴です。';
const OGP_IMAGE_URL = `${import.meta.env.VITE_SITE_URL || 'https://lp.nomikoi.app'}/ogp-image.png?v=1`;

const storeLinks = [
  {
    href: APP_STORE_URL,
    ariaLabel: 'App Store',
    modifier: 'apple',
    label: 'App Store',
  },
  {
    href: GOOGLE_PLAY_URL,
    ariaLabel: 'Google Play',
    modifier: 'google-play',
    label: 'Google Play',
  },
] as const;

const footerLinks = [
  { label: "利用規約", href: "https://nomikoi.app/lp/caution" },
  { label: "プライバシーポリシー", href: "https://nomikoi.app/lp/privacy-policy" },
  { label: "特定商取引法に基づく表記", href: "https://nomikoi.app/lp/notations" },
  { label: "会社概要", href: "https://nomikoi.app/lp/company" },
];

const legalNotices = [
  "インターネット異性紹介事業 届出済 受理番号：碑文谷25-040338",
  "電気通信事業 届出済 届出番号：A-07-22597",
];

const schemaData = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  "name": "nomikoi",
  "description": PAGE_DESCRIPTION,
  "url": import.meta.env.VITE_SITE_URL || "https://lp.nomikoi.app",
  "applicationCategory": "SocialNetworkingApplication",
  "operatingSystem": "iOS, Android",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "JPY"
  }
};

const Home = () => {
  useEffect(() => {
    saveUtmParams();

    const utmParams = getMergedUtmParams();

    trackMetaPageView({
      source: utmParams.source,
      medium: utmParams.medium,
      campaign: utmParams.campaign,
      content: utmParams.content,
    });

    if (utmParams.source === UTM_SOURCE.TIKTOK && typeof window !== 'undefined' && window.ttq) {
      window.ttq.track('PageView', {
        utm_source: utmParams.source,
        utm_medium: utmParams.medium,
        utm_campaign: utmParams.campaign || '',
        utm_content: utmParams.content || '',
      });
    }
  }, []);

  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        keywords="nomikoi, 呑恋, のみこい, お酒 マッチングアプリ, 恋活, 婚活"
        openGraph={{
          title: PAGE_TITLE,
          description: PAGE_DESCRIPTION,
          image: OGP_IMAGE_URL,
        }}
        twitter={{
          title: PAGE_TITLE,
          description: PAGE_DESCRIPTION,
          image: OGP_IMAGE_URL,
        }}
        schemaData={schemaData}
      />
      <main className="download-lp" aria-label="nomikoi download landing page">
        <section className="download-lp__screen">
          <img
            className="download-lp__logo"
            src="/nomikoi-logo-trimmed.png"
            alt="nomikoi"
          />

          <p className="download-lp__tagline">理想の飲み相手がすぐ見つかる</p>

          <div className="download-lp__store-buttons" aria-label="アプリストアリンク">
            {storeLinks.map((storeLink) => (
              <a
                key={storeLink.label}
                className={`download-lp__store-link download-lp__store-link--${storeLink.modifier}`}
                href={storeLink.href}
                aria-label={storeLink.ariaLabel}
              >
                <span className="download-lp__store-badge">
                  <span
                    className={`download-lp__store-icon download-lp__store-icon--${storeLink.modifier}`}
                    aria-hidden="true"
                  />
                  <span className="download-lp__store-label">{storeLink.label}</span>
                </span>
              </a>
            ))}
          </div>

          <footer className="download-lp__footer">
            <nav className="download-lp__footer-links" aria-label="フッターリンク">
              {footerLinks.map((link) => (
                <a href={link.href} key={link.label}>
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="download-lp__footer-separator" aria-hidden="true" />

            <div className="download-lp__legal">
              {legalNotices.map((notice) => (
                <p key={notice}>{notice}</p>
              ))}
            </div>

            <p className="download-lp__copyright">© MAVNIQ Inc.</p>
          </footer>
        </section>
      </main>
    </>
  );
};

export default Home;
